/**
 * General localStorage utilities for the application
 * Provides type-safe, SSR-safe localStorage operations with error handling
 */

/**
 * Check if localStorage is available
 * @returns true if localStorage is supported and available
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    return typeof window !== "undefined" && "localStorage" in window;
  } catch (error) {
    return false;
  }
};

/**
 * Get an item from localStorage
 * @param key - The localStorage key
 * @returns The parsed value or null if not found/error
 */
export const getFromLocalStorage = <T = any>(key: string): T | null => {
  try {
    if (!isLocalStorageAvailable()) {
      return null;
    }

    const item = localStorage.getItem(key);
    if (item === null) {
      return null;
    }

    return JSON.parse(item) as T;
  } catch (error) {
    return null;
  }
};

/**
 * Set an item in localStorage
 * @param key - The localStorage key
 * @param value - The value to store (will be JSON stringified)
 * @returns true if successful, false otherwise
 */
export const setToLocalStorage = <T = any>(key: string, value: T): boolean => {
  try {
    if (!isLocalStorageAvailable()) {
      return false;
    }

    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Remove an item from localStorage
 * @param key - The localStorage key to remove
 * @returns true if successful, false otherwise
 */
export const removeFromLocalStorage = (key: string): boolean => {
  try {
    if (!isLocalStorageAvailable()) {
      return false;
    }

    localStorage.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Check if a key exists in localStorage
 * @param key - The localStorage key to check
 * @returns true if key exists, false otherwise
 */
export const existsInLocalStorage = (key: string): boolean => {
  try {
    if (!isLocalStorageAvailable()) {
      return false;
    }

    return localStorage.getItem(key) !== null;
  } catch (error) {
    return false;
  }
};
